import React, { useState, useEffect, useCallback } from 'react';
import ConfirmationDialog from '../../settings/components/ConfirmationDialog';
import {
  Box, Typography, Button, Paper, IconButton,
  TextField, Select, MenuItem, FormControl, InputLabel, Grid, CircularProgress, Alert, Link,
  List, ListItem, ListItemText, Divider as MuiDivider, Checkbox, FormGroup, FormControlLabel,
  Snackbar // Added Tooltip for displaying original role
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon,
  Save as SaveIcon, UploadFile as UploadFileIcon,
  RemoveCircleOutline as RemoveCircleOutlineIcon, // Icon for removing locations
  Email as EmailIcon, // Icon for requesting email
  Info as InfoIcon // Icon for showing original role
} from '@mui/icons-material';
import { useLoading } from '../../context/LoadingContext'; // Assuming context exists
import { Document, Page, pdfjs } from 'react-pdf'; // Import react-pdf components
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import DocumentSelector from '../common/DocumentSelector';

// Configure react-pdf worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

// Define possible statuses - should match backend enum
const STATUS_OPTIONS = ['Not Started', 'Requested', 'Pending Review', 'Approved', 'Denied', 'Expired', 'Error']; // Added 'Not Started'
const CHECK_TYPE_OPTIONS = ['MVR', 'Criminal', 'Comprehensive', 'Other']; // Match backend enum
const ROLE_OPTIONS = ['admin', 'dispatcher', 'answeringService', 'driver']; // Standard roles

// Initial state for a single location entry
const initialLocationState = { city: '', countyOrParish: '', state: '' };
// Initial state for address
const initialAddressState = { street1: '', street2: '', city: '', state: '', zip: '', country: 'US' };
// Initial state for assigned roles
const initialRolesState = { admin: false, dispatcher: false, answeringService: false, driver: false, other: false, otherRoleName: '', originalRole: '' };

// Helper function to map role from upload to checkbox roles
const mapRoleToCheckboxes = (role) => {
  if (!role) return { ...initialRolesState };
  
  const roleStr = role.toLowerCase().trim();
  const result = { ...initialRolesState, originalRole: role };
  
  // If role is "owner", select all roles
  if (roleStr === 'owner') {
    return {
      admin: true,
      dispatcher: true,
      answeringService: true,
      driver: true,
      other: false,
      otherRoleName: '',
      originalRole: role
    };
  }
  
  // Map standard roles - case insensitive matching
  if (roleStr === 'admin') result.admin = true;
  else if (roleStr === 'dispatcher') result.dispatcher = true;
  else if (roleStr === 'answeringservice' || roleStr === 'answering service') result.answeringService = true;
  else if (roleStr === 'driver') result.driver = true;
  // For non-standard roles, set as "other"
  else if (roleStr && !ROLE_OPTIONS.map(r => r.toLowerCase()).includes(roleStr)) {
    result.other = true;
    result.otherRoleName = role;
  }
  
  return result;
};

function BackgroundCheckTab({ application, isReadOnly }) { // Add isReadOnly prop
  const [records, setRecords] = useState([]);
  const [selectedRecordId, setSelectedRecordId] = useState(''); // State for selected record in dropdown
  const [selectedRecord, setSelectedRecord] = useState(null); // Full data for selected record
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [formData, setFormData] = useState({}); // Form data for the right panel
  const [fileToUpload, setFileToUpload] = useState(null);
  const [fetchError, setFetchError] = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [newLocation, setNewLocation] = useState(initialLocationState); // State for adding new location
  const { startLoading } = useLoading();

  // PDF Viewer states
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [pdfError, setPdfError] = useState(false);
  const [scale, setScale] = useState(1.0); // Default scale

  // A replacement report archives the one it supersedes instead of erasing it,
  // so a record can hold several documents. This tracks which one is on screen.
  const [viewedBcDoc, setViewedBcDoc] = useState(null);

  // Snackbar states
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };


  const getAuthHeader = useCallback((contentType = 'application/json') => {
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };
    if (contentType) {
      headers['Content-Type'] = contentType;
    }
    return headers;
  }, []);

  // Current document plus every superseded one, newest first
  const bcVersions = React.useMemo(() => {
    if (!selectedRecord) return [];
    const history = (selectedRecord.documentHistory || [])
      .filter((h) => h && h.path)
      .slice()
      .sort((a, b) => new Date(b.replacedAt || b.uploadedAt || 0) - new Date(a.replacedAt || a.uploadedAt || 0));
    return [
      ...(selectedRecord.documentPath
        ? [{
            path: selectedRecord.documentPath,
            current: true,
            uploadedAt: selectedRecord.documentUploadedAt
          }]
        : []),
      ...history.map((h) => ({ ...h, current: false }))
    ];
  }, [selectedRecord]);

  const viewedBcPath = viewedBcDoc?.path || selectedRecord?.documentPath;

  const fetchBackgroundChecks = useCallback(async () => {
    if (!application?._id) return;
    const stopLoading = startLoading();
    setFetchError(null);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/applications/${application._id}/background-checks`, {
        headers: getAuthHeader(null) // No content-type needed for GET
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch background checks: ${response.statusText}`);
      }
      const data = await response.json();
      setRecords(data);
      // If no record is selected, select the first one if available
      if (!selectedRecordId && data.length > 0) {
          setSelectedRecordId(data[0]._id);
      } else if (selectedRecordId && !data.find(rec => rec._id === selectedRecordId)) {
          // If the previously selected record was deleted, select the first one
          setSelectedRecordId(data.length > 0 ? data[0]._id : '');
      }
    } catch (err) {
      console.error("Error fetching background checks:", err);
      setFetchError(err.message || 'Could not load background check records.');
    } finally {
      stopLoading();
    }
  }, [application?._id, startLoading, getAuthHeader, selectedRecordId]); // Added selectedRecordId dependency

  useEffect(() => {
    fetchBackgroundChecks();
  }, [fetchBackgroundChecks]);

  // Update selectedRecord and formData when selectedRecordId or records change
  useEffect(() => {
      const record = records.find(rec => rec._id === selectedRecordId) || null;
      setSelectedRecord(record);
      
      // Initialize formData based on the selected record or empty if adding new
      let initialFormData;
      
      if (record) {
          // Saved Assigned Roles selections take priority — reviewers edit
          // these on this tab and they must not revert on reload. The
          // employeeRole mapping is only a default for records that have
          // never had any role selected (e.g. fresh uploads, where the
          // schema stores an all-false assignedRoles subdocument).
          const savedRoles = record.assignedRoles || {};
          const hasSavedSelection = ROLE_OPTIONS.some(r => savedRoles[r]) ||
              savedRoles.other || savedRoles.otherRoleName;
          const finalAssignedRoles = hasSavedSelection
              ? { ...initialRolesState, ...savedRoles }
              : mapRoleToCheckboxes(record.employeeRole);

          // Always ensure originalRole is preserved
          finalAssignedRoles.originalRole = record.employeeRole || '';

          initialFormData = {
              ...record,
              address: record.address ? { ...initialAddressState, ...record.address } : { ...initialAddressState },
              assignedRoles: finalAssignedRoles, // Use the merged roles
              criminalRecordLocations: Array.isArray(record.criminalRecordLocations) ? record.criminalRecordLocations : []
          };
      } else {
          initialFormData = {
              personName: '',
              email: '',
              phone: '',
              address: { ...initialAddressState },
              assignedRoles: { ...initialRolesState },
              dateOfBirth: '', ssnLast4: '', checkType: 'Comprehensive',
              status: 'Requested', dateRequested: new Date().toISOString().split('T')[0],
              dateCompleted: '', dateExpires: '', dateReviewed: '', reviewNotes: '',
              documentPath: '', provider: '', mvrStatus: '', criminalStatus: '',
              criminalRecordLocations: [],
              otherDetails: ''
          };
      }

      setFormData(initialFormData);
      setFileToUpload(null); // Reset file upload when switching records
      setSaveError(null); // Reset save error
      setNewLocation(initialLocationState); // Reset new location input
      setPdfError(false); // Reset PDF error state
      setNumPages(null); // Reset PDF page count
      setPageNumber(1); // Reset PDF page number
  }, [selectedRecordId, records]);


  const handleFormChange = (event) => {
    const { name, value, type, checked } = event.target;

    // Handle nested address fields
    if (name.startsWith('address.')) {
        const field = name.split('.')[1];
        setFormData(prev => ({
            ...prev,
            address: {
                ...(prev.address || initialAddressState),
                [field]: value
            }
        }));
    }
    // Handle nested assignedRoles fields (checkboxes and text input)
    else if (name.startsWith('assignedRoles.')) {
         const field = name.split('.')[1];
         const fieldValue = type === 'checkbox' ? checked : value;
         setFormData(prev => ({
             ...prev,
             assignedRoles: {
                 ...(prev.assignedRoles || initialRolesState),
                 [field]: fieldValue
             }
         }));
    }
    // Handle standard fields
    else {
        setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleFileChange = (event) => {
    setFileToUpload(event.target.files[0]);
  };

  // --- Handlers for Criminal Record Locations ---
  const handleNewLocationChange = (event) => {
    const { name, value } = event.target;
    setNewLocation(prev => ({ ...prev, [name]: value }));
  };

  const handleAddLocation = () => {
    if (newLocation.city || newLocation.countyOrParish || newLocation.state) {
      setFormData(prev => ({
        ...prev,
        criminalRecordLocations: [...(prev.criminalRecordLocations || []), { ...newLocation }]
      }));
      setNewLocation(initialLocationState); // Reset input fields
    }
  };

  const handleRemoveLocation = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      criminalRecordLocations: (prev.criminalRecordLocations || []).filter((_, index) => index !== indexToRemove)
    }));
  };
  // --- End Handlers for Criminal Record Locations ---


  const handleSave = async () => {
    const stopLoading = startLoading();
    setSaveError(null);
    let endpoint = '';
    let method = '';
    const isAddingNew = !selectedRecordId; // Determine if adding new based on selectedRecordId

    // Prepare data, ensuring dates are handled correctly (send null if empty)
    const dataToSend = { ...formData };
    ['dateOfBirth', 'dateCompleted', 'dateExpires', 'dateReviewed', 'dateRequested'].forEach(field => {
        if (!dataToSend[field]) {
            dataToSend[field] = null;
        }
    });
     // Ensure criminalRecordLocations is an array
     dataToSend.criminalRecordLocations = Array.isArray(dataToSend.criminalRecordLocations) ? dataToSend.criminalRecordLocations : [];
     // Ensure address object exists
     dataToSend.address = dataToSend.address || initialAddressState;
     // Ensure assignedRoles object exists and originalRole is preserved
     dataToSend.assignedRoles = dataToSend.assignedRoles || initialRolesState;
     
     // Preserve the original employeeRole in both places for consistency
     if (dataToSend.employeeRole && !dataToSend.assignedRoles.originalRole) {
         dataToSend.assignedRoles.originalRole = dataToSend.employeeRole;
     } else if (dataToSend.assignedRoles.originalRole && !dataToSend.employeeRole) {
         dataToSend.employeeRole = dataToSend.assignedRoles.originalRole;
     }


    // Handle file upload separately if a file is selected
    let uploadedFilePath = formData.documentPath; // Keep existing path unless new file uploaded
    if (fileToUpload) {
        const fileFormData = new FormData();
        fileFormData.append('backgroundCheckDoc', fileToUpload); // Use a specific field name
        // Include application ID, company name, and person name for backend path
        fileFormData.append('applicationId', application._id);
        fileFormData.append('companyName', application.companyName); // Assuming companyName is on application object
        fileFormData.append('personName', dataToSend.personName); // Assuming personName is in formData

        try {
            // Use the backend upload endpoint
            const uploadResponse = await fetch(`${process.env.REACT_APP_API_URL}/api/applications/${application._id}/upload-background-check`, { // New upload endpoint
                method: 'POST',
                headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }, // No Content-Type for FormData
                body: fileFormData,
            });
            if (!uploadResponse.ok) {
                 const errorText = await uploadResponse.text(); // Get error text
                 console.error("Upload response error text:", errorText);
                 throw new Error(`File upload failed: ${uploadResponse.statusText}`);
            }
            const uploadResult = await uploadResponse.json();
            uploadedFilePath = uploadResult.filePath; // Get the path from the upload response
            dataToSend.documentPath = uploadedFilePath; // Update dataToSend with the new path
            showSnackbar('File uploaded successfully!', 'success');
        } catch (err) {
            console.error("File upload error:", err);
            setSaveError(err.message || 'File upload failed.');
            stopLoading();
            return; // Stop save process if upload fails
        }
    }


    if (isAddingNew) {
      endpoint = `${process.env.REACT_APP_API_URL}/api/applications/${application._id}/background-checks`; // POST to add
      method = 'POST';
    } else {
      endpoint = `${process.env.REACT_APP_API_URL}/api/background-checks/${selectedRecordId}`; // PUT to update
      method = 'PUT';
    }

    try {
      const response = await fetch(endpoint, {
        method: method,
        headers: getAuthHeader(), // application/json
        body: JSON.stringify(dataToSend)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to ${isAddingNew ? 'add' : 'save'} record.`);
      }
      const result = await response.json();
      showSnackbar(`Record ${isAddingNew ? 'added' : 'saved'} successfully!`, 'success');
      await fetchBackgroundChecks(); // Refresh list
      if (isAddingNew && result._id) {
          setSelectedRecordId(result._id); // Select the newly added record
      }
    } catch (err) {
      console.error(`Error ${isAddingNew ? 'adding' : 'saving'} background check:`, err);
      setSaveError(err.message || `Could not ${isAddingNew ? 'add' : 'save'} record.`);
      showSnackbar(`Failed to ${isAddingNew ? 'add' : 'save'} record: ${err.message}`, 'error');
    } finally {
      stopLoading();
    }
  };

  const handleDeleteClick = () => {
    if (!selectedRecordId) return;
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    const stopLoading = startLoading();
    setFetchError(null); // Use fetchError for list-level errors
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/background-checks/${selectedRecordId}`, {
        method: 'DELETE',
        headers: getAuthHeader(null)
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete record.');
      }
      showSnackbar('Record deleted successfully!', 'success');
      setSelectedRecordId(''); // Deselect the record
      await fetchBackgroundChecks(); // Refresh list
    } catch (err) {
      console.error("Error deleting background check:", err);
      setFetchError(err.message || 'Could not delete record.');
      showSnackbar(`Failed to delete record: ${err.message}`, 'error');
    } finally {
      stopLoading();
      setDeleteDialogOpen(false);
    }
  };

  // Remove a single uploaded report without touching the record itself
  const handleDeleteDocumentVersion = async (version) => {
    const stopLoading = startLoading();
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/background-checks/${selectedRecordId}/document-versions`,
        { method: 'DELETE', headers: getAuthHeader(), body: JSON.stringify({ path: version.path }) }
      );
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete document version.');
      }
      setViewedBcDoc(null); // fall back to whatever is current after the delete
      setPdfError(false);
      setPageNumber(1);
      await fetchBackgroundChecks();
      showSnackbar('Document version removed.', 'success');
    } catch (err) {
      console.error('Error deleting background check document version:', err);
      showSnackbar(err.message || 'Could not remove that document version.', 'error');
    } finally {
      stopLoading();
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  // Handle PDF document loading
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPdfError(false);
  };

  // Handle PDF document loading error
  const onDocumentLoadError = (error) => {
    console.error('Error loading PDF:', error);
    setPdfError(true);
  };

  // Function to create the full document URL
  const getDocumentUrl = (filePath) => {
    if (!filePath) return null;
    const baseUrl = process.env.NODE_ENV === 'production' ? '' : process.env.REACT_APP_API_URL; // Use backend API URL

    // Parse the path to extract company name, employee name, and filename
    // Expected format: uploads\CompanyName\backgroundchecks\EmployeeName\filename.pdf
    // or: CompanyName/backgroundchecks/EmployeeName/filename.pdf
    const pathParts = filePath.replace(/^uploads[\\/]/, '').replace(/\\/g, '/').split('/');
    
    // These files are auth-gated server-side; links/PDF viewers can't send
    // headers, so the JWT rides along as a query param.
    const token = localStorage.getItem('token');
    const tokenParam = token ? `?token=${encodeURIComponent(token)}` : '';

    if (pathParts.length >= 4) {
      const companyName = encodeURIComponent(pathParts[0]);
      const employeeName = encodeURIComponent(pathParts[2]);
      const fileName = encodeURIComponent(pathParts[3]);

      // Use the new dedicated route for background check files
      return `${baseUrl}/api/applications/background-check-file/${companyName}/${employeeName}/${fileName}${tokenParam}`;
    }

    // Fallback to the old method if path format doesn't match expected structure
    console.warn('Unexpected file path format:', filePath);
    return `${baseUrl}/uploads/${filePath.replace(/^uploads[\\/]/, '').replace(/\\/g, '/')}${tokenParam}`;
  };

  // Handle requesting a new Background Check email
  const handleRequestBackgroundCheckEmail = async () => {
    if (!application?._id) return;
    const stopLoading = startLoading();
    setSaveError(null); // Use saveError for email request errors

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/applications/${application._id}/request-background-check`, { // New backend endpoint
        method: 'POST',
        headers: getAuthHeader(),
        body: JSON.stringify({
          email: application.email, // Include applicant email
          companyName: application.companyName // Include company name
        })
      });

      const responseData = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(responseData.message || `Failed to request background check email.`);
      }

      showSnackbar(responseData.message || 'Background check request email sent successfully!', 'success');
      // Optionally update record status to 'Requested' for all records or a new one
      await fetchBackgroundChecks(); // Refresh records to show updated status
    } catch (err) {
      console.error("Error requesting background check email:", err);
      setSaveError(err.message || 'Could not request background check email.');
      showSnackbar(`Failed to request background check email: ${err.message}`, 'error');
    } finally {
      stopLoading();
    }
  };


  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>Background Checks</Typography>

      {/* Request Background Check Email Button */}
      <Button
        variant="contained"
        startIcon={<EmailIcon />}
        onClick={handleRequestBackgroundCheckEmail}
        disabled={isReadOnly}
        sx={{ mb: 2, mr: 2 }}
      >
        Request Background Check Email
      </Button>

      {/* Add New Background Check Record Button */}
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={() => setSelectedRecordId('')} // Clear selection to add new
        disabled={isReadOnly}
        sx={{ mb: 2 }}
      >
        Add New Background Check Record
      </Button>


      {fetchError && <Alert severity="error" sx={{ mb: 2 }}>{fetchError}</Alert>}
      {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>} {/* Display save errors */}

      <Grid container spacing={3}>
        {/* Left Panel - PDF Viewer and Dropdown */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, height: '100%', minHeight: 400, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6">
                Background Check Document
              </Typography>
              {/* Zoom Controls (Optional, similar to InsuranceTab) */}
            </Box>

            <MuiDivider sx={{ mb: 2 }} />

            {/* Dropdown to select record */}
            <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Select Person</InputLabel>
                <Select
                    value={selectedRecordId}
                    label="Select Person"
                    onChange={(e) => setSelectedRecordId(e.target.value)}
                    disabled={records.length === 0}
                >
                    {records.map((record) => (
                        <MenuItem key={record._id} value={record._id}>
                            {record.personName || 'Unnamed Record'}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            {/* Every uploaded report is kept — step through them here */}
            {bcVersions.length > 0 && (
                <Box sx={{ mb: 2 }}>
                    <DocumentSelector
                        versions={bcVersions}
                        label="Background check"
                        isReadOnly={isReadOnly}
                        onVersionChange={setViewedBcDoc}
                        onDelete={handleDeleteDocumentVersion}
                    />
                </Box>
            )}

            {/* Approval needs these on the record; owner fields prefill from the application on save */}
            {selectedRecord && (() => {
                const address = formData.address || {};
                const missing = [
                    !String(formData.ssnLast4 || '').trim() && 'SSN last 4',
                    !formData.dateExpires && 'expiration date',
                    !String(formData.phone || '').trim() && 'phone',
                    !String(formData.email || '').trim() && 'email',
                    !(address.street1 && address.city && address.state && address.zip) && 'address'
                ].filter(Boolean);
                return missing.length > 0 ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        Still needed before this person can be activated (and, for the owner, before approval): {missing.join(', ')}.
                    </Alert>
                ) : null;
            })()}

            {/* Document viewer: photos as images, everything else through the PDF viewer */}
            {viewedBcPath && /.(jpe?g|png|gif|webp|heic)$/i.test(viewedBcPath) ? (
                <Box sx={{ flexGrow: 1, overflow: 'auto', textAlign: 'center' }}>
                    <img
                        src={getDocumentUrl(viewedBcPath)}
                        alt="Background check"
                        style={{ maxWidth: '100%', height: 'auto' }}
                        onError={() => setPdfError(true)}
                    />
                    {pdfError && (
                        <Typography color="error" sx={{ mt: 1 }}>Error loading document. The file may be missing or inaccessible.</Typography>
                    )}
                </Box>
            ) : viewedBcPath ? (
                pdfError ? (
                    <Box sx={{ p: 2, textAlign: 'center' }}>
                        <Typography color="error">
                            Error loading document. The file may be missing or inaccessible.
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{ flexGrow: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Document
                            file={getDocumentUrl(viewedBcPath)}
                            onLoadSuccess={onDocumentLoadSuccess}
                            onLoadError={onDocumentLoadError}
                            loading={<CircularProgress />}
                        >
                            <Page
                                pageNumber={pageNumber}
                                scale={scale}
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                            />
                        </Document>
                        {numPages && (
                            <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Button
                                    disabled={pageNumber <= 1}
                                    onClick={() => setPageNumber(pageNumber - 1)}
                                >
                                    Previous
                                </Button>
                                <Typography>
                                    Page {pageNumber} of {numPages}
                                </Typography>
                                <Button
                                    disabled={pageNumber >= numPages}
                                    onClick={() => setPageNumber(pageNumber + 1)}
                                >
                                    Next
                                </Button>
                            </Box>
                        )}
                         <Box sx={{ mt: 2 }}>
                            <Link
                              href={getDocumentUrl(viewedBcPath)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              Download Document
                            </Link>
                          </Box>
                    </Box>
                )
            ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                    <Typography>
                        {selectedRecordId ? 'No document uploaded for this record.' : 'Select a record or add a new one to view document.'}
                    </Typography>
                </Box>
            )}

          </Paper>
        </Grid>

        {/* Right Panel - Record Form */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              {selectedRecordId ? 'Edit Background Check Record' : 'Add New Background Check Record'}
            </Typography>
            <MuiDivider sx={{ mb: 2 }} />

            {/* Form Fields */}
            <Grid container spacing={2} sx={{ mt: 1 }}>
                {/* --- Standard Fields --- */}
                <Grid item xs={12} sm={6}>
                  <TextField label="Person's Name" name="personName" value={formData.personName || ''} onChange={handleFormChange} fullWidth required disabled={isReadOnly} />
                </Grid>
                 <Grid item xs={12} sm={6}>
                  <TextField label="Date of Birth" name="dateOfBirth" type="date" value={formData.dateOfBirth?.split('T')[0] || ''} onChange={handleFormChange} fullWidth InputLabelProps={{ shrink: true }} disabled={isReadOnly} />
                </Grid>
                 <Grid item xs={12} sm={6}>
                  <TextField label="SSN (Last 4)" name="ssnLast4" value={formData.ssnLast4 || ''} onChange={handleFormChange} fullWidth inputProps={{ maxLength: 4 }} disabled={isReadOnly} />
                </Grid>
                 <Grid item xs={12} sm={6}>
                  <TextField label="Email" name="email" type="email" value={formData.email || ''} onChange={handleFormChange} fullWidth disabled={isReadOnly} />
                </Grid>
                 <Grid item xs={12} sm={6}>
                  <TextField label="Phone" name="phone" type="tel" value={formData.phone || ''} onChange={handleFormChange} fullWidth disabled={isReadOnly} />
                </Grid>

                {/* --- Address Fields --- */}
                 <Grid item xs={12}><Typography variant="subtitle2" sx={{mt:1}}>Address</Typography></Grid>
                 <Grid item xs={12}>
                     <TextField label="Street 1" name="address.street1" value={formData.address?.street1 || ''} onChange={handleFormChange} fullWidth disabled={isReadOnly} />
                 </Grid>
                 <Grid item xs={12}>
                     <TextField label="Street 2" name="address.street2" value={formData.address?.street2 || ''} onChange={handleFormChange} fullWidth disabled={isReadOnly} />
                 </Grid>
                 <Grid item xs={12} sm={4}>
                     <TextField label="City" name="address.city" value={formData.address?.city || ''} onChange={handleFormChange} fullWidth disabled={isReadOnly} />
                 </Grid>
                 <Grid item xs={12} sm={3}>
                     <TextField label="State" name="address.state" value={formData.address?.state || ''} onChange={handleFormChange} fullWidth disabled={isReadOnly} />
                 </Grid>
                 <Grid item xs={12} sm={3}>
                     <TextField label="Zip Code" name="address.zip" value={formData.address?.zip || ''} onChange={handleFormChange} fullWidth disabled={isReadOnly} />
                 </Grid>
                 <Grid item xs={12} sm={2}>
                     <TextField label="Country" name="address.country" value={formData.address?.country || 'US'} onChange={handleFormChange} fullWidth disabled={isReadOnly} />
                 </Grid>
                 {/* --- End Address Fields --- */}

                 {/* --- Assigned Roles --- */}
                 <Grid item xs={12}>
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                       <Typography variant="subtitle2" sx={{mt:1}}>Assigned Roles (for User Creation)</Typography>
                       
                       {/* Original Role Button - Always show if originalRole exists */}
                       {formData.assignedRoles?.originalRole && (
                          <Button
                             variant="outlined"
                             size="small"
                             startIcon={<InfoIcon fontSize="small" />}
                             sx={{
                               ml: { xs: 0, sm: 2 },
                               mt: 1,
                               textTransform: 'none',
                               borderRadius: '16px',
                               backgroundColor: '#f0f7ff',
                               '&:hover': {
                                 backgroundColor: '#e1f0ff',
                               }
                             }}
                             color="primary"
                          >
                             Original Role: {formData.assignedRoles.originalRole}
                          </Button>
                       )}
                    </Box>
                    <FormGroup row>
                        {ROLE_OPTIONS.map(role => (
                             <FormControlLabel
                                key={role}
                                control={
                                    <Checkbox
                                        checked={formData.assignedRoles?.[role] || false}
                                        onChange={handleFormChange}
                                        name={`assignedRoles.${role}`}
                                        disabled={isReadOnly}
                                        size="small"
                                    />
                                }
                                label={role.charAt(0).toUpperCase() + role.slice(1)} // Capitalize
                             />
                        ))}
                         <FormControlLabel
                            control={
                                <Checkbox
                                    checked={formData.assignedRoles?.other || false}
                                    onChange={handleFormChange}
                                    name="assignedRoles.other"
                                    disabled={isReadOnly}
                                    size="small"
                                />
                            }
                            label="Other"
                         />
                         {/* Removed the redundant display of original role here */}
                    </FormGroup>
                    {formData.assignedRoles?.other && (
                         <TextField
                            label="Other Role Name"
                            name="assignedRoles.otherRoleName"
                            value={formData.assignedRoles?.otherRoleName || ''}
                            onChange={handleFormChange}
                            fullWidth
                            size="small"
                            sx={{mt: 1}}
                            disabled={isReadOnly}
                         />
                    )}
                 </Grid>
                 {/* --- End Assigned Roles --- */}


                 <Grid item xs={12} sm={6}>
                   <FormControl fullWidth>
                     <InputLabel>Check Type</InputLabel>
                     <Select label="Check Type" name="checkType" value={formData.checkType || 'Comprehensive'} onChange={handleFormChange} disabled={isReadOnly}>
                       {CHECK_TYPE_OPTIONS.map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                     </Select>
                   </FormControl>
                </Grid>
                 <Grid item xs={12} sm={6}>
                   <FormControl fullWidth>
                     <InputLabel>Status</InputLabel>
                     <Select label="Status" name="status" value={formData.status || 'Requested'} onChange={handleFormChange} disabled={isReadOnly}>
                       {STATUS_OPTIONS.map(option => <MenuItem key={option} value={option}>{option}</MenuItem>)}
                     </Select>
                   </FormControl>
                </Grid>
                 <Grid item xs={12} sm={6}>
                  <TextField label="Date Requested" name="dateRequested" type="date" value={formData.dateRequested?.split('T')[0] || ''} onChange={handleFormChange} fullWidth InputLabelProps={{ shrink: true }} disabled={isReadOnly} />
                </Grid>
                 <Grid item xs={12} sm={6}>
                  <TextField label="Date Completed" name="dateCompleted" type="date" value={formData.dateCompleted?.split('T')[0] || ''} onChange={handleFormChange} fullWidth InputLabelProps={{ shrink: true }} disabled={isReadOnly} />
                </Grid>
                 <Grid item xs={12} sm={6}>
                  <TextField label="Date Expires" name="dateExpires" type="date" value={formData.dateExpires?.split('T')[0] || ''} onChange={handleFormChange} fullWidth InputLabelProps={{ shrink: true }} disabled={isReadOnly} />
                </Grid>
                 <Grid item xs={12} sm={6}>
                  <TextField label="Date Reviewed" name="dateReviewed" type="date" value={formData.dateReviewed?.split('T')[0] || ''} onChange={handleFormChange} fullWidth InputLabelProps={{ shrink: true }} disabled={isReadOnly} />
                </Grid>
                 <Grid item xs={12} sm={6}>
                  <TextField label="Provider" name="provider" value={formData.provider || ''} onChange={handleFormChange} fullWidth disabled={isReadOnly} />
                </Grid>
                 <Grid item xs={12}>
                  <TextField label="Review Notes" name="reviewNotes" value={formData.reviewNotes || ''} onChange={handleFormChange} fullWidth multiline rows={3} disabled={isReadOnly} />
                </Grid>
                 <Grid item xs={12}>
                  <TextField label="MVR Status/Notes" name="mvrStatus" value={formData.mvrStatus || ''} onChange={handleFormChange} fullWidth multiline rows={2} disabled={isReadOnly} />
                </Grid>
                 <Grid item xs={12}>
                  <TextField label="Criminal Status/Notes" name="criminalStatus" value={formData.criminalStatus || ''} onChange={handleFormChange} fullWidth multiline rows={2} disabled={isReadOnly} />
                </Grid>

                {/* --- Criminal Record Locations Section --- */}
                <Grid item xs={12}>
                  <Typography variant="subtitle1" gutterBottom>Criminal Record Locations</Typography>
                  <List dense>
                    {(formData.criminalRecordLocations || []).map((loc, index) => (
                      <ListItem
                        key={index}
                        secondaryAction={ !isReadOnly ? ( // Disable remove button if read-only
                          <IconButton edge="end" aria-label="delete location" onClick={() => handleRemoveLocation(index)} disabled={isReadOnly}>
                            <RemoveCircleOutlineIcon />
                          </IconButton>
                        ) : null }
                      >
                        <ListItemText primary={`${loc.city || 'N/A'}, ${loc.countyOrParish || 'N/A'}, ${loc.state || 'N/A'}`} />
                      </ListItem>
                    ))}
                  </List>
                  {!isReadOnly && ( // Hide add location section if read-only
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mt: 1 }}>
                      <TextField label="City" name="city" value={newLocation.city} onChange={handleNewLocationChange} size="small" sx={{ flexGrow: 1 }} disabled={isReadOnly}/>
                      <TextField label="County/Parish" name="countyOrParish" value={newLocation.countyOrParish} onChange={handleNewLocationChange} size="small" sx={{ flexGrow: 1 }} disabled={isReadOnly}/>
                      <TextField label="State" name="state" value={newLocation.state} onChange={handleNewLocationChange} size="small" sx={{ width: '80px' }} disabled={isReadOnly}/>
                      <Button onClick={handleAddLocation} variant="outlined" size="small" sx={{ height: '40px' }} disabled={isReadOnly}>Add Loc</Button>
                    </Box>
                  )}
                </Grid>
                {/* --- End Criminal Record Locations Section --- */}

                <Grid item xs={12}>
                  <TextField label="Other Details" name="otherDetails" value={formData.otherDetails || ''} onChange={handleFormChange} fullWidth multiline rows={2} disabled={isReadOnly} />
                </Grid>

                {/* File Upload/Link */}
                 <Grid item xs={12}>
                    <Typography variant="subtitle2" gutterBottom>Document</Typography>
                    {formData.documentPath && (
                        <Box mb={1}>
                            <Link href={getDocumentUrl(formData.documentPath)} target="_blank" rel="noopener noreferrer">
                                View Current Document ({formData.documentPath.split('/').pop()})
                            </Link>
                        </Box>
                    )}
                    {!isReadOnly && ( // Hide upload button if read-only
                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<UploadFileIcon />}
                            disabled={isReadOnly}
                        >
                            {fileToUpload ? fileToUpload.name : (formData.documentPath ? 'Add Newer Document' : 'Upload Document')}
                            <input
                                type="file"
                                hidden
                                onChange={handleFileChange}
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" // Specify acceptable file types
                            />
                        </Button>
                    )}
                    {formData.documentPath && !isReadOnly && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            The current document is kept — you can step back to it in the version selector.
                        </Typography>
                    )}
                 </Grid>

              </Grid>

              {/* Save and Delete Buttons */}
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
                  {!isReadOnly && (
                      <>
                          {selectedRecordId && ( // Only show delete for existing records
                              <Button onClick={handleDeleteClick} variant="outlined" color="error" startIcon={<DeleteIcon />} disabled={isReadOnly}>
                                  Delete Record
                              </Button>
                          )}
                          <Button onClick={handleSave} variant="contained" color="primary" startIcon={<SaveIcon />} disabled={isReadOnly}>
                              {selectedRecordId ? 'Save Changes' : 'Add Record'}
                          </Button>
                      </>
                  )}
              </Box>

          </Paper>
        </Grid>
      </Grid>

      {/* Snackbar for notifications */}
      <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar}>
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title="Delete Background Check"
        content="Are you sure you want to delete this background check record? This action cannot be undone."
        confirmColor="error"
      />

    </Box>
  );
}

export default BackgroundCheckTab;
